import {
  Divider,
  Flex,
  Grid,
  Heading,
  Typography,
} from "@sidelinesports/fan-design-system";
import type { ApiPlayerDetails } from "../../../../api";
import { Dialog, Markdown } from "../../../../components";
import styles from "./PlayerProfile.module.css";

export type PlayerProfileProps = {
  player: ApiPlayerDetails;
  trigger: React.ReactNode;
};

export const PlayerProfile = ({ player, trigger }: PlayerProfileProps) => {
  return (
    <Dialog.Root>
      {trigger}
      <Dialog.Content size="sm" name="PlayerProfileDialog">
        <Dialog.Body>
          <Grid gap={16} columns="repeat(autofit, minmax(300px, 1fr))">
            <Hero player={player} />
            <Grid gap={16}>
              {player.seasonStats && player.seasonStats.length > 0 && (
                <Grid gap={8} columns="repeat(3, minmax(max-content, 1fr))">
                  {player.seasonStats.map((stat) => (
                    <Grid key={stat.code}>
                      <Heading level={2} as="div">
                        {stat.value}
                      </Heading>
                      <Heading level={5} as="div">
                        {stat.label}
                      </Heading>
                    </Grid>
                  ))}
                </Grid>
              )}
              {player.bio && (
                <Grid gap={8}>
                  <Heading level={2}>More</Heading>
                  <Markdown>{player.bio}</Markdown>
                </Grid>
              )}
              {player.stats && (
                <Grid gap={8}>
                  <Heading level={2}>Statistics</Heading>
                  <Markdown>{player.stats}</Markdown>
                </Grid>
              )}
              {player.facts && (
                <Grid gap={8}>
                  <Heading level={2}>Fun Facts</Heading>
                  <Markdown>{player.facts}</Markdown>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Dialog.Body>
      </Dialog.Content>
    </Dialog.Root>
  );
};

const Hero = ({ player }: { player: ApiPlayerDetails }) => {
  return (
    <div
      className={styles.hero}
      style={
        {
          "--background-image-url": `url(${player.headshot.url})`,
        } as React.CSSProperties
      }
    >
      <div className={styles["hero-image"]} />
      <Flex
        className={styles["hero-content"]}
        w="100%"
        h="100%"
        direction="column"
        justify="between"
        align="center"
        gap={16}
      >
        <Flex className={styles["top-shader"]} justify="end" w="100%" p={16}>
          <Dialog.Close />
        </Flex>
        <Grid gap={4} w="100%">
          <HeroName player={player} />
          <HeroNumberAndPosition player={player} />
        </Grid>
      </Flex>
    </div>
  );
};

const HeroName = ({ player }: { player: ApiPlayerDetails }) => (
  <Grid p={16} w="100%">
    <div className={styles["player-first-name"]}>{player.firstName}</div>
    <div className={styles["player-last-name"]}>{player.lastName}</div>
  </Grid>
);

const HeroNumberAndPosition = ({ player }: { player: ApiPlayerDetails }) => (
  <Flex
    className={styles["hero-number-and-position"]}
    direction="row"
    align="center"
    gap={8}
    w="100%"
    px={16}
  >
    <Divider variant="accent" />
    <Typography size="lg">{player.number}</Typography>
    <Typography className={styles.dot} size="lg">
      •
    </Typography>
    <Typography size="lg" className={styles["player-position"]}>
      {player.position}
    </Typography>
    <Divider variant="accent" />
  </Flex>
);
